import React, { useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Check,
  X,
  Clock,
  Loader2,
  CheckCircle2,
  CreditCard,
  Package,
  Truck,
  Home,
  XCircle,
  Ban,
  Eye,
  Edit,
  Trash,
  Search,
  QrCode,
  Link,
  Wallet,
  ShoppingCart,
  Pencil,
  PenLine,
} from "lucide-react";
import { format } from "date-fns";
import { TypeTableProps } from "@/App/Plans/Table";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { TableDataProps } from "@/pages/SalesList";
import {
  getSaleStatusColor,
  getSaleStatusBadge,
  SALE_STATUS,
} from "@/constants/saleStatus";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { registerHasAprovedPayment, updateSaleStatus, regeneratePix } from "@/Api/sales";
import { EditSaleModal } from "@/components/EditSaleModal";
import { CancelSaleModal } from "@/components/CancelSaleModal";
import { toast } from "sonner";
import { getSelectBankAccounts } from "@/Api/bankAccounts";
import { StepDialog } from "@/components/ui/step-dialog";
import { PixDialog } from "@/components/ui/pix-dialog";
import { PixButton } from "@/components/ui/pix-button";
import { Mask } from "@/utils/mask";
import { SALE_STEPS, PIX_STATUS, PAYMENT_FORMS, SALE_TYPE_ID } from "@/utils/constants";
import { ReceiptDialog } from "@/components/ui/receipt-dialog";
import { TransactionCodeModal } from "./components/TransactionCodeModal";
import { CancelRequestModal } from "./components/CancelRequestModal";
import { EditRequestModal } from "./components/EditRequestModal";
import { EndorsementRequestModal } from "@/App/Endorsements/RequestModal";
import { getEndorsementSalesStatus } from "@/Api/endorsements";
import { PERMISSIONS } from "@/constants/permissions";
import { If } from "@/components/If";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PixPayment } from "@/components/ui/pix-payment";
import {
  ActionsPopover,
  type ActionItem,
} from "@/components/ui/actions-popover";
import { usePermissions } from "@/hooks/usePermissions";
import { useSettings } from "@/hooks/useSettings";
import { getSaleChangeRequestWindow } from "@/utils/saleChangeRequestWindow";
import { useAuth } from "@/hooks/auth";

interface Address {
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
}

interface ProcessStepSale {
  id: number;
  sale_id: number;
  sale_step_id: number;
  process_status_id: number;
  address_id: number | null;
  created_at: string;
  sale_step: {
    id: number;
    name: string;
  };
  process_status: {
    id: number;
    name: string;
  };
  address: Address | null;
  process_step_sale_chats: Array<{
    id: number;
    message: string;
    created_at: string;
    author: {
      name: string;
    };
  }>;
}

interface TableDataExtendedProps {
  onSort?: (column: string, direction: "asc" | "desc") => void;
  sortConfig?: {
    column: string;
    direction: "asc" | "desc";
  };
}

export const TableData = ({
  dataApi,
  pageActions,
  onSort,
  sortConfig,
}: TypeTableProps<TableDataProps> & {
  pageActions: { refresh: () => Promise<void>; silentRefresh: () => Promise<void> };
} & TableDataExtendedProps) => {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [selectedStep, setSelectedStep] = useState<ProcessStepSale | null>(
    null
  );
  const [selectedStepSale, setSelectedStepSale] = useState<TableDataProps | null>(null);
  const [selectedSale, setSelectedSale] = useState<TableDataProps | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [saleIdForApproval, setSaleIdForApproval] = useState<number | null>(
    null
  );
  const [saleForApproval, setSaleForApproval] = useState<TableDataProps | null>(
    null
  );

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [saleToEdit, setSaleToEdit] = useState<TableDataProps | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [saleToCancel, setSaleToCancel] = useState<TableDataProps | null>(null);
  const [cancelRequestModalOpen, setCancelRequestModalOpen] = useState(false);
  const [saleForCancelRequest, setSaleForCancelRequest] = useState<TableDataProps | null>(null);
  const [editRequestModalOpen, setEditRequestModalOpen] = useState(false);
  const [saleForEditRequest, setSaleForEditRequest] = useState<TableDataProps | null>(null);
  // LOT-34: endossamento só em venda Endosso; some o botão enquanto a solicitação está pendente/aprovada
  const [endorsementSaleId, setEndorsementSaleId] = useState<number | null>(null);
  const [endorsementStatusBySale, setEndorsementStatusBySale] = useState<Record<number, string>>({});
  const [showPixPaymentModal, setShowPixPaymentModal] = useState(false);
  const [selectedPixTransaction, setSelectedPixTransaction] =
    useState<any>(null);
  const [availablePixTransactions, setAvailablePixTransactions] = useState<
    any[]
  >([]);
  const [showPixSelectionModal, setShowPixSelectionModal] = useState(false);
  const [paymentDetailsSale, setPaymentDetailsSale] = useState<TableDataProps | null>(null);
  const [showPaymentLinksModal, setShowPaymentLinksModal] = useState(false);
  const [saleForPaymentLinks, setSaleForPaymentLinks] = useState<TableDataProps | null>(null);
  const [bankAccounts, setBankAccounts] = useState<
    Array<{ id: number; name: string; surname: string }>
  >([]);
  const { hasPermission, isAdmin, isTenantUser } = usePermissions();
  useSettings();
  const user = useAuth();
  const sellerId = user?.user?.seller?.id;
  const isSeller = !!sellerId;

  // Ninguém pode editar/deletar diretamente - todos devem fazer solicitação
  const permissionToEdit = false;
  const permissionToDelete = false;

  // Qualquer usuário com permissão pode solicitar edição/cancelamento
  const canRequestChanges = hasPermission("edit-sales") || hasPermission("delete-sales");

  /**
   * Admins não ficam sujeitos ao prazo de solicitação definido em
   * Configurações do Sistema — mesma regra aplicada no backend.
   */
  const isExemptFromRequestTimeLimit = isAdmin() || isTenantUser();

  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const { data } = await getSelectBankAccounts();
        if (data?.response) {
          setBankAccounts(data.response);
        }
      } catch (error) {
        console.error("Erro ao buscar contas bancárias:", error);
      }
    };

    fetchBankAccounts();
  }, []);

  const handleSort = (column: string) => {
    if (!onSort) return;

    const direction =
      sortConfig?.column === column && sortConfig?.direction === "asc"
        ? "desc"
        : "asc";

    onSort(column, direction);
  };

  const getSortIcon = (column: string) => {
    if (sortConfig?.column !== column) {
      return null;
    }
    return sortConfig?.direction === "asc" ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  const toggleRow = (id: number) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const findUpdatedStep = useCallback(
    (stepId: number) => {
      if (!dataApi?.data) return null;

      for (const item of dataApi.data) {
        if (!item.process_step_sales) continue;

        const foundStep = item.process_step_sales.find(
          (step: ProcessStepSale) => step.id === stepId
        );

        if (foundStep) return foundStep;
      }

      return null;
    },
    [dataApi?.data]
  );

  const loadEndorsementStatuses = useCallback(async () => {
    const ids = (dataApi?.data ?? [])
      .filter((sale: TableDataProps) => sale.sale_type_id === SALE_TYPE_ID.ENDOSSO)
      .map((sale: TableDataProps) => sale.id);

    if (ids.length === 0) return;

    const { ok, data } = await getEndorsementSalesStatus(ids);
    if (ok) setEndorsementStatusBySale(data.response ?? {});
  }, [dataApi?.data]);

  useEffect(() => {
    loadEndorsementStatuses();
  }, [loadEndorsementStatuses]);

  const handleRefresh = async () => {
    if (refreshing) return;

    setRefreshing(true);
    try {
      const currentStepId = selectedStep?.id;
      await pageActions.silentRefresh();

      if (currentStepId) {
        const updatedStep = findUpdatedStep(currentStepId);
        if (updatedStep) {
          setSelectedStep(updatedStep);
        }
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleUpdateStatus = async (stepId: number, statusId: number, trackingCode?: string) => {
    try {
      await updateSaleStatus(stepId, statusId, trackingCode);
      await handleRefresh();
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };

  const handlePaymentApproval = async (
    id: number,
    approved: boolean,
    transactionCodes?: any,
    trackingCode?: string
  ) => {
    try {
      await registerHasAprovedPayment({
        id: id.toString(),
        itsAproved: approved,
        sale_code_transaction: transactionCodes || null,
        tracking_code: trackingCode,
      });
      await handleRefresh();

      if (approved) {
        toast.success("Pagamento aprovado com sucesso!");
      } else {
        toast.error("Pagamento recusado!");
      }
    } catch (error) {
      toast.error("Erro ao processar pagamento");
    }
  };

  const openTransactionModal = (id: number, sale: TableDataProps) => {
    setSaleIdForApproval(id);
    setSaleForApproval(sale);
    setShowTransactionModal(true);
  };

  const handleTransactionConfirm = async (
    transactionCodes: Array<{
      payment_form_id: number;
      code_transaction: string;
      bank_account_id?: number;
      payer_name?: string;
      date_hour?: string;
      observation?: string;
    }>,
    trackingCode?: string
  ) => {
    if (saleIdForApproval) {
      await handlePaymentApproval(saleIdForApproval, true, transactionCodes, trackingCode);
    }
  };

  const getStepStatusColor = (statusId: number, stepId: number) => {
    if ((stepId === 3 || stepId === 7) && statusId === 3) {
      return "bg-red-500";
    }

    switch (statusId) {
      case 1:
        return "bg-yellow-500";
      case 2:
        return "bg-blue-500";
      case 3:
        return "bg-green-500";
      case 4:
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getStepIcon = (stepId: number, statusId: number) => {
    const iconProps = {
      className: cn("w-5 h-5", statusId === 3 ? "text-white" : "text-white/60"),
    };

    switch (stepId) {
      case 1: // Aguardando pagamento
        return <CreditCard {...iconProps} />;
      case 2: // Pagamento aprovado
        return <CheckCircle2 {...iconProps} />;
      case 3: // Pagamento recusado
        return <XCircle {...iconProps} />;
      case 4: // Em separação
        return <Package {...iconProps} />;
      case 5: // Em rota de entrega
        return <Truck {...iconProps} />;
      case 6: // Entregue
        return <Home {...iconProps} />;
      case 7: // Cancelado
        return <Ban {...iconProps} />;
      default:
        return statusId === 3 ? (
          <Check {...iconProps} />
        ) : (
          <Clock {...iconProps} />
        );
    }
  };

  const getStepStatusIcon = (statusId: number, stepId: number) => {
    if ((stepId === 3 || stepId === 7) && statusId === 3) {
      return <X className="w-4 h-4 text-red-600" />;
    }

    switch (statusId) {
      case 1:
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 2:
        return <Loader2 className="w-4 h-4 text-blue-600" />;
      case 3:
        return <Check className="w-4 h-4 text-green-600" />;
      case 4:
        return <X className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStepStatusName = (
    status: string,
    stepId: number,
    statusId: number
  ) => {
    if ((stepId === 3 || stepId === 7) && statusId === 3) {
      return "Recusado";
    }
    return status;
  };

  useEffect(() => {
    if (selectedStep && dataApi?.data) {
      const updatedStep = findUpdatedStep(selectedStep.id);
      if (updatedStep) {
        setSelectedStep(updatedStep);
      }
    }
  }, [dataApi?.data, selectedStep, findUpdatedStep]);

  const handleShowPixQRCode = async (e: React.MouseEvent, sale: TableDataProps) => {
    e.stopPropagation();

    const pixTransactions = sale.transactions?.filter(
      (t) => t.pix_copy_and_paste
    ) || [];

    if (pixTransactions.length === 0) {
      const hasPixForm = sale.payment_forms?.some(
        (form) =>
          form.payment_form.id === PAYMENT_FORMS.PIX ||
          form.payment_form.id === PAYMENT_FORMS.PIX_BTG
      );

      if (hasPixForm) {
        try {
          toast.loading("Gerando PIX...", { id: "regenerate-pix" });
          const { ok, data } = await regeneratePix(sale.uuid);
          toast.dismiss("regenerate-pix");

          if (ok && data?.response?.transactions?.length > 0) {
            const newPixTransactions = data.response.transactions.filter(
              (t: any) => t.pix_copy_and_paste
            );
            if (newPixTransactions.length === 1) {
              setSelectedPixTransaction({
                ...newPixTransactions[0],
                saleId: sale.id,
              });
              setShowPixPaymentModal(true);
            } else if (newPixTransactions.length > 1) {
              setAvailablePixTransactions(
                newPixTransactions.map((t: any) => ({
                  ...t,
                  saleId: sale.id,
                }))
              );
              setShowPixSelectionModal(true);
            }
            await handleRefresh();
          } else {
            toast.error("Não foi possível gerar o PIX");
          }
        } catch (error) {
          toast.dismiss("regenerate-pix");
          toast.error("Erro ao gerar PIX");
        }
      }
      return;
    }

    if (pixTransactions.length === 1) {
      setSelectedPixTransaction({
        ...pixTransactions[0],
        saleId: sale.id,
      });
      setShowPixPaymentModal(true);
    } else if (pixTransactions.length > 1) {
      setAvailablePixTransactions(
        pixTransactions.map((t) => ({
          ...t,
          saleId: sale.id,
        }))
      );
      setShowPixSelectionModal(true);
    }
  };

  const handleSelectPixTransaction = (transaction: any) => {
    setSelectedPixTransaction(transaction);
    setShowPixSelectionModal(false);
    setShowPixPaymentModal(true);
  };

  const handleClosePixSelectionModal = () => {
    setShowPixSelectionModal(false);
    setAvailablePixTransactions([]);
  };

  const handlePixPaymentConfirmed = async () => {
    setShowPixPaymentModal(false);
    await handleRefresh();
    toast.success("Pagamento confirmado!");
  };

  const handleClosePixModal = () => {
    setShowPixPaymentModal(false);
    setSelectedPixTransaction(null);
  };

  const handleCopyPaymentLink = async (sale: TableDataProps) => {
    const pixTransactions = sale.transactions?.filter(t => t.pix_copy_and_paste) || [];

    // Se tiver mais de uma transação PIX, abre o modal
    if (pixTransactions.length > 1) {
      setSaleForPaymentLinks(sale);
      setShowPaymentLinksModal(true);
      return;
    }

    // Se tiver apenas uma ou nenhuma, copia o link único
    const baseUrl = window.location.origin;
    const paymentUrl = `${baseUrl}/public/payment/${sale.uuid}`;

    try {
      await navigator.clipboard.writeText(paymentUrl);
      toast.success("Link de pagamento copiado!");
    } catch (error) {
      toast.error("Erro ao copiar link de pagamento");
    }
  };

  const handleCopySinglePaymentLink = async (transactionIndex: number) => {
    if (!saleForPaymentLinks) return;

    const baseUrl = window.location.origin;
    const paymentUrl = `${baseUrl}/public/payment/${saleForPaymentLinks.uuid}?pix=${transactionIndex}`;

    try {
      await navigator.clipboard.writeText(paymentUrl);
      toast.success(`Link do PIX #${transactionIndex + 1} copiado!`);
    } catch (error) {
      toast.error("Erro ao copiar link de pagamento");
    }
  };

  const handleClosePaymentLinksModal = () => {
    setShowPaymentLinksModal(false);
    setSaleForPaymentLinks(null);
  };

  const handleViewReceipt = (sale: TableDataProps) => {
    setSelectedSale(sale);
    setShowReceipt(true);
  };

  const handleEditSale = (sale: TableDataProps) => {
    setSaleToEdit(sale);
    setEditModalOpen(true);
  };

  const handleCancelSale = (sale: TableDataProps) => {
    setSaleToCancel(sale);
    setCancelModalOpen(true);
  };

  const handleRequestCancel = (sale: TableDataProps) => {
    setSaleForCancelRequest(sale);
    setCancelRequestModalOpen(true);
  };

  const handleRequestEdit = (sale: TableDataProps) => {
    setSaleForEditRequest(sale);
    setEditRequestModalOpen(true);
  };

  const handleSaleCancelled = async () => {
    await handleRefresh();
  };

  const handleSaleUpdated = () => {
    handleRefresh();
  };

  const hasNonPixPaymentForms = (sale: TableDataProps) => {
    if (!sale.payment_forms || sale.payment_forms.length === 0) {
      return false;
    }

    return sale.payment_forms.some(
      (form) =>
        form.payment_form_id !== PAYMENT_FORMS.PIX &&
        form.payment_form_id !== PAYMENT_FORMS.PIX_BTG
    );
  };

  const getPixTransactionStatus = (sale: TableDataProps) => {
    if (!sale.transactions || sale.transactions.length === 0) {
      return {
        hasPixTransactions: false,
        allPaid: false,
        paidCount: 0,
        totalCount: 0,
      };
    }

    const pixTransactions = sale.transactions.filter(
      (t) => t.pix_copy_and_paste
    );
    const paidTransactions = pixTransactions.filter(
      (t) => t.status === PIX_STATUS.PAID
    );

    return {
      hasPixTransactions: pixTransactions.length > 0,
      allPaid:
        pixTransactions.length > 0 &&
        paidTransactions.length === pixTransactions.length,
      paidCount: paidTransactions.length,
      totalCount: pixTransactions.length,
    };
  };

  const hasPixPaymentForm = (sale: TableDataProps) => {
    return sale.payment_forms?.some(
      (form) =>
        form.payment_form.id === PAYMENT_FORMS.PIX ||
        form.payment_form.id === PAYMENT_FORMS.PIX_BTG
    );
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead
              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort("sale_id")}
            >
              <div className="flex items-center gap-1">
                <Search className="h-3 w-3 text-gray-400" />
                ID{getSortIcon("sale_id")}
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort("client")}
            >
              <div className="flex items-center gap-1">
                <Search className="h-3 w-3 text-gray-400" />
                Cliente{getSortIcon("client")}
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort("seller")}
            >
              <div className="flex items-center gap-1">
                <Search className="h-3 w-3 text-gray-400" />
                Vendedor{getSortIcon("seller")}
              </div>
            </TableHead>
            <TableHead>Loteria</TableHead>
            <TableHead>Tipo de Venda</TableHead>
            <TableHead>Forma de Pagamento</TableHead>
            <TableHead
              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort("total")}
            >
              <div className="flex items-center gap-1">
                <Search className="h-3 w-3 text-gray-400" />
                Valor Total{getSortIcon("total")}
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-center"
              onClick={() => handleSort("sale_status_id")}
            >
              <div className="flex items-center justify-center gap-1">
                <Search className="h-3 w-3 text-gray-400" />
                Status{getSortIcon("sale_status_id")}
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-center"
              onClick={() => handleSort("created_at")}
            >
              <div className="flex items-center justify-center gap-1">
                <Search className="h-3 w-3 text-gray-400" />
                Data/Hora da Compra{getSortIcon("created_at")}
              </div>
            </TableHead>
            <TableHead className="w-[72px] text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data.map((item) => (
            <React.Fragment key={item.id}>
              <TableRow
                className={cn(
                  "cursor-pointer transition-colors",
                  getSaleStatusColor(item.sale_status_id)
                )}
                onClick={() => toggleRow(item.id)}
              >
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">#{item?.id}</span>
                    {!!item?.is_pre_sale && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-purple-600 dark:text-purple-400 whitespace-nowrap">
                        <ShoppingCart className="h-3 w-3" />
                        Pré-venda
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>{item?.client?.name}</TableCell>
                <TableCell>{item?.seller?.user?.name}</TableCell>
                <TableCell>
                  {item?.products
                    ?.map((product) => product?.contest?.lottery?.name)
                    .filter(
                      (value, index, self) => self.indexOf(value) === index
                    )
                    .join(", ") || "-"}
                </TableCell>
                <TableCell>
                  {item?.sale_type?.name ? String(item.sale_type.name) : "-"}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {item?.payment_forms && item?.payment_forms.length > 0 ? (
                      <div className="flex flex-col">
                        {item.payment_forms.length === 1 ? (
                          <span>
                            {item.payment_forms[0]?.payment_form?.name}
                          </span>
                        ) : (
                          <>
                            <span className="text-xs text-muted-foreground">
                              Múltiplas formas:
                            </span>
                            {Array.from(
                              new Set(
                                item.payment_forms.map(
                                  (form) => form.payment_form.name
                                )
                              )
                            ).map((name, idx) => (
                              <span key={idx} className="text-xs">
                                • {name}
                              </span>
                            ))}
                          </>
                        )}
                      </div>
                    ) : (
                      "-"
                    )}
                    <PixButton sale={item} onClick={handleShowPixQRCode} />
                  </div>
                </TableCell>
                <TableCell>
                  {Mask.formatCurrency(parseFloat(item?.total))}
                </TableCell>
                <TableCell className="text-center">
                  <span
                    className={cn(
                      "px-2.5 py-0.5 rounded text-xs font-medium",
                      getSaleStatusBadge(item.sale_status_id)
                    )}
                  >
                    {item.sale_status?.name}
                  </span>
                  {(() => {
                    const steps = item.process_step_sales || [];
                    const hasCanceled = steps.some(
                      (s) => s.process_status_id === 4
                    );

                    const hasShowUpdatedBy =
                      item.sale_status_id == SALE_STATUS.EDITED ||
                      item.sale_status_id == SALE_STATUS?.CANCELED ||
                      item.sale_status_id == SALE_STATUS.CANCELLED_WITH_PIX ||
                      item?.sale_status_id == SALE_STATUS.FAILED;

                    if (hasShowUpdatedBy && item.updated_by) {
                      return (
                        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                          <div>Por: {item.updated_by.name}</div>
                          <div className="text-[10px] text-gray-500">
                            {new Date(item.updated_at).toLocaleDateString(
                              "pt-BR"
                            )}{" "}
                            às{" "}
                            {new Date(item.updated_at).toLocaleTimeString(
                              "pt-BR",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </div>
                        </div>
                      );
                    }

                    if (hasCanceled) return null;

                    const totalSteps = steps.length;
                    const finishedSteps = steps.filter(
                      (s) => s.process_status_id === 3
                    ).length;
                    const percent =
                      totalSteps > 0
                        ? Math.round((finishedSteps / totalSteps) * 100)
                        : 0;

                    const progressBarColor =
                      percent === 100 ? "bg-green-500" : "bg-yellow-400";

                    return (
                      <div className="w-full flex flex-col items-center mt-2">
                        <div className="w-full bg-gray-200 rounded h-2 relative">
                          <div
                            className={`${progressBarColor} h-2 rounded transition-all`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600 mt-1">
                          {percent}%
                        </span>
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-center">
                  <div className="text-sm">
                    <div>
                      {new Date(item?.created_at).toLocaleDateString("pt-BR")}
                    </div>
                    <div className="text-gray-500 text-xs">
                      {new Date(item?.created_at).toLocaleTimeString("pt-BR", {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="w-[72px] whitespace-nowrap text-center align-middle">
                  <div className="flex items-center justify-center gap-1">
                    {expandedRow === item.id ? (
                      <ChevronUp
                        onClick={() => toggleRow(item.id)}
                        className="w-4 h-4"
                      />
                    ) : (
                      <ChevronDown
                        onClick={() => toggleRow(item.id)}
                        className="w-4 h-4"
                      />
                    )}
                    {(() => {
                      const pixStatus = getPixTransactionStatus(item);
                      const hasPixForm = hasPixPaymentForm(item);
                      const hasPaymentDetails = !!item.payment_forms?.some(
                        (pf) => pf.observation || pf.payer_name || pf.date_hour
                      );

                      // Solicitar edição/cancelamento (vendedor: próprias vendas, admin/gerente: todas)
                      // Bloqueia apenas vendas canceladas (4, 6) ou com falha (3)
                      const canRequestChangesForSale =
                        canRequestChanges &&
                        (!isSeller || item.seller_id === sellerId) &&
                        item.sale_status_id !== SALE_STATUS.CANCELED &&
                        item.sale_status_id !== SALE_STATUS.CANCELLED_WITH_PIX &&
                        item.sale_status_id !== SALE_STATUS.FAILED;

                      /**
                       * Prazo definido pelo admin em Configurações do Sistema,
                       * contado a partir da criação da venda. A ação continua
                       * visível quando o prazo expira, porém desabilitada e com
                       * o motivo no tooltip.
                       */
                      const editRequestWindow = getSaleChangeRequestWindow(
                        "edit",
                        item.created_at,
                        isExemptFromRequestTimeLimit
                      );

                      const cancelRequestWindow = getSaleChangeRequestWindow(
                        "cancel",
                        item.created_at,
                        isExemptFromRequestTimeLimit
                      );

                      // Endossamento: só venda Endosso, vendedor nas próprias vendas e sem solicitação ativa
                      const endorsementStatus = endorsementStatusBySale[item.id];
                      const canRequestEndorsement =
                        item.sale_type_id === SALE_TYPE_ID.ENDOSSO &&
                        (!isSeller || item.seller_id === sellerId) &&
                        hasPermission(PERMISSIONS.VIEW_ENDORSEMENT_REQUESTS) &&
                        endorsementStatus !== "pending" &&
                        endorsementStatus !== "approved" &&
                        item.sale_status_id !== SALE_STATUS.CANCELED &&
                        item.sale_status_id !== SALE_STATUS.CANCELLED_WITH_PIX &&
                        item.sale_status_id !== SALE_STATUS.FAILED;

                      const actions: ActionItem[] = [
                        {
                          key: "request-endorsement",
                          hidden: !canRequestEndorsement,
                          icon: <PenLine className="h-4 w-4 !text-blue-500" />,
                          label: "Solicitar Endossamento",
                          onClick: () => setEndorsementSaleId(item.id),
                        },
                        {
                          key: "pix-qrcode",
                          hidden:
                            !pixStatus.hasPixTransactions && !hasPixForm,
                          icon: <QrCode className="h-4 w-4" />,
                          label: pixStatus.allPaid
                            ? "PIX Pago"
                            : pixStatus.hasPixTransactions
                            ? `Ver QR Code PIX ${
                                pixStatus.totalCount > 1
                                  ? `(${pixStatus.totalCount} disponíveis)`
                                  : ""
                              }`
                            : "Gerar QR Code PIX",
                          onClick: (e) => handleShowPixQRCode(e, item),
                        },
                        {
                          key: "show-receipt",
                          hidden: !hasPermission("show-sale-details"),
                          icon: <Eye className="h-4 w-4" />,
                          label: "Ver Comprovante",
                          onClick: () => handleViewReceipt(item),
                        },
                        {
                          key: "payment-details",
                          hidden: !hasPaymentDetails,
                          icon: <Wallet className="h-4 w-4" />,
                          label: "Detalhes do Pagamento",
                          onClick: () => setPaymentDetailsSale(item),
                        },
                        {
                          key: "copy-payment-link",
                          hidden: !hasPixForm,
                          icon: <Link className="h-4 w-4" />,
                          label: "Copiar Link de Pagamento",
                          onClick: () => handleCopyPaymentLink(item),
                        },
                        {
                          key: "edit-sale",
                          hidden: !permissionToEdit,
                          icon: <Edit className="h-4 w-4 !text-blue-500" />,
                          label: "Editar Venda",
                          onClick: () => handleEditSale(item),
                        },
                        {
                          key: "cancel-sale",
                          hidden: !permissionToDelete,
                          icon: <Trash className="h-4 w-4 !text-red-500" />,
                          label: "Cancelar Venda",
                          onClick: () => handleCancelSale(item),
                        },
                        {
                          key: "request-edit",
                          hidden: !canRequestChangesForSale,
                          disabled: !editRequestWindow.allowed,
                          title: editRequestWindow.message ?? undefined,
                          icon: <Edit className="h-4 w-4 !text-blue-500" />,
                          label: editRequestWindow.allowed
                            ? "Solicitar Edição"
                            : "Solicitar Edição (prazo expirado)",
                          onClick: () => handleRequestEdit(item),
                        },
                        {
                          key: "request-cancel",
                          hidden: !canRequestChangesForSale,
                          disabled: !cancelRequestWindow.allowed,
                          title: cancelRequestWindow.message ?? undefined,
                          icon: <Trash className="h-4 w-4 !text-red-500" />,
                          label: cancelRequestWindow.allowed
                            ? "Solicitar Cancelamento"
                            : "Solicitar Cancelamento (prazo expirado)",
                          onClick: () => handleRequestCancel(item),
                        },
                      ];

                      return <ActionsPopover actions={actions} align="end" />;
                    })()}
                  </div>

                  {/* <ShowBtn
                    params={{
                      func: (): any => {
                        return Promise.resolve({
                          data: { response: item.products },
                          ok: true,
                        });
                      },
                      funcParams: {},
                    }}
                    render={(products) => (
                      <div className="space-y-4">
                        {products.map((product: any) => (
                          <div key={product.id} className="border-b pb-3">
                            <div className="flex items-center justify-between gap-4">
                              <p className="font-medium text-lg">
                                {product?.product_name} -{" "}
                                {product?.product_classification?.name}
                              </p>
                              {product?.contest?.lottery && (
                                <div
                                  className="w-4 h-4 rounded-full"
                                  style={{
                                    backgroundColor:
                                      product?.contest?.lottery?.color,
                                  }}
                                  title={product?.contest?.lottery?.name}
                                />
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground mt-2">
                              <p>Concurso: {product.contest?.name}</p>
                              <p>
                                Loteria:{" "}
                                {product?.contest?.lottery?.name || "N/A"}
                              </p>
                            </div>
                            <div className="flex justify-between mt-2 text-sm">
                              <span>
                                Quantidade: {product?.pivot?.quantity}
                              </span>
                              <span>
                                Preço:{" "}
                                {Mask.formatCurrency(
                                  parseFloat(product?.pivot?.price_total)
                                )}
                              </span>
                            </div>
                          </div>
                        ))}
                        {item?.circuit_route?.routes &&
                          item?.circuit_route?.routes?.length > 0 && (
                            <div className="mt-4">
                              <div className="text-sm text-muted-foreground mt-2">
                                <p>
                                  Nome da Rota: {item?.circuit_route?.title}
                                </p>
                                <p>
                                  Início:{" "}
                                  {new Date(
                                    item?.circuit_route?.starts + "T00:00:00"
                                  ).toLocaleDateString("pt-BR", {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                  })}
                                </p>
                              </div>
                              <div className="overflow-auto max-h-40 mt-3">
                                {item?.circuit_route &&
                                  item?.circuit_route?.routes &&
                                  item?.circuit_route?.routes?.length > 0 &&
                                  item?.circuit_route?.routes?.map(
                                    (route, index) => (
                                      <div
                                        key={index}
                                        className="border-b pb-2 mb-2"
                                      >
                                        <p className="text-sm text-muted-foreground">
                                          Parada {index + 1}:{" "}
                                          {route?.address?.addressLineOne}
                                        </p>
                                      </div>
                                    )
                                  )}
                              </div>

                              <a
                                href={
                                  item?.circuit_route?.routes?.[0]?.webAppLink
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline font-semibold"
                              >
                                Ver Rota
                              </a>
                            </div>
                          )}
                      </div>
                    )}
                    labels={{
                      title: "Produtos da Venda",
                      description: "Detalhes dos produtos adquiridos",
                    }}
                  /> */}
                </TableCell>
              </TableRow>

              {expandedRow === item.id && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <If condition={hasPermission("show-sale-steps")}>
                      <div className="p-6 bg-zinc-100 dark:bg-zinc-800 rounded-lg">
                        <div className="flex items-center justify-between mb-6">
                          <h4 className="text-lg font-semibold">
                            Etapas do Processo
                          </h4>
                        </div>

                        <div className="relative">
                          <div className="absolute top-5 left-[2.25rem] right-[2.25rem] h-0.5 bg-gray-200 dark:bg-gray-700" />
                          <div className="relative z-10 flex justify-between">
                            {[...(item.process_step_sales as ProcessStepSale[])]
                              .sort((a, b) => a.sale_step_id - b.sale_step_id)
                              .map(
                              (step) => (
                                <React.Fragment key={step.id}>
                                  <div className="flex flex-col items-center">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedStep(step);
                                        setSelectedStepSale(item);
                                      }}
                                      className={cn(
                                        "size-10 rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center transition-colors",
                                        getStepStatusColor(
                                          step.process_status.id,
                                          step.sale_step.id
                                        )
                                      )}
                                    >
                                      {getStepIcon(
                                        step.sale_step.id,
                                        step.process_status.id
                                      )}
                                    </button>
                                    <div className="mt-2 flex flex-col items-center gap-1">
                                      <p className="text-xs text-muted-foreground mb-1">
                                        {new Date(
                                          step?.created_at
                                        ).toLocaleString("pt-BR", {
                                          day: "2-digit",
                                          month: "2-digit",
                                          year: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                      </p>
                                      <p className="text-sm font-medium max-w-[120px] text-center">
                                        {step.sale_step.name}
                                      </p>
                                      <span
                                        className={cn(
                                          "flex items-center gap-1 text-xs px-2 py-1 rounded-full",
                                          step.process_status.id === 1
                                            ? "bg-yellow-100 text-yellow-700"
                                            : step.process_status.id === 2
                                            ? "bg-blue-100 text-blue-700"
                                            : step.process_status.id === 4
                                            ? "bg-red-100 text-red-700"
                                            : (step.sale_step.id === 3 ||
                                                step.sale_step.id === 7) &&
                                              step.process_status.id === 3
                                            ? "bg-red-100 text-red-700"
                                            : "bg-green-100 text-green-700"
                                        )}
                                      >
                                        {getStepStatusIcon(
                                          step.process_status.id,
                                          step.sale_step.id
                                        )}
                                        {getStepStatusName(
                                          step.process_status.name,
                                          step.sale_step.id,
                                          step.process_status.id
                                        )}
                                      </span>
                                    </div>
                                    {step.sale_step_id ===
                                      SALE_STEPS.WAITING_PAYMENT &&
                                      step.process_status_id === 2 && (
                                        <div className="flex gap-1 mt-2">
                                          {(hasNonPixPaymentForms(item) || item.sale_type_id === SALE_TYPE_ID.SEDEX) ? (
                                            <div className="flex gap-2">
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-7 text-xs bg-green-50 hover:bg-green-100 text-green-600"
                                                onClick={() =>
                                                  openTransactionModal(
                                                    item?.id,
                                                    item
                                                  )
                                                }
                                              >
                                                <Check className="w-3 h-3 mr-1" />{" "}
                                                Aprovar
                                              </Button>
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-7 text-xs bg-red-50 hover:bg-red-100 text-red-600"
                                                onClick={() =>
                                                  handleCancelSale(item)
                                                }
                                              >
                                                <XCircle className="w-3 h-3 mr-1" />{" "}
                                                Recusar
                                              </Button>
                                            </div>
                                          ) : null}
                                          {(() => {
                                            const pixStatus =
                                              getPixTransactionStatus(item);
                                            if (!pixStatus.hasPixTransactions)
                                              return null;

                                            return (
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className={`h-7 text-xs mt-1 ${
                                                  pixStatus.allPaid
                                                    ? "bg-green-50 hover:bg-green-100 text-green-600"
                                                    : "bg-indigo-50 hover:bg-indigo-100 text-indigo-600"
                                                }`}
                                                onClick={(e) =>
                                                  handleShowPixQRCode(e, item)
                                                }
                                              >
                                                <QrCode className="w-3 h-3 mr-1" />
                                                {pixStatus.allPaid
                                                  ? "Pago"
                                                  : pixStatus.totalCount > 1
                                                  ? `Ver PIX (${pixStatus.totalCount})`
                                                  : "Ver PIX"}
                                              </Button>
                                            );
                                          })()}
                                        </div>
                                      )}
                                  </div>
                                  {/* Indicador de Edição - aparece após o step "Pagamento aprovado" quando a venda foi editada */}
                                  {step.sale_step_id === SALE_STEPS.PAYMENT_APPROVED &&
                                    (item.sale_status_id === SALE_STATUS.EDITED ||
                                      item.sale_status_id === SALE_STATUS.EDITED_AND_APPROVED) &&
                                    item.updated_by && (
                                      <div className="flex flex-col items-center mx-2">
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <div className="size-10 rounded-full bg-blue-600 border-2 border-white dark:border-gray-800 flex items-center justify-center cursor-pointer">
                                                <Pencil className="w-5 h-5 text-white" />
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent className="bg-gray-900 text-white border-gray-700">
                                              <div className="text-sm">
                                                <p className="font-medium">
                                                  {new Date(item.updated_at).toLocaleString("pt-BR", {
                                                    day: "2-digit",
                                                    month: "2-digit",
                                                    year: "numeric",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                  })}
                                                </p>
                                                <p>Editado</p>
                                                <p>Por: "{item.updated_by.name}"</p>
                                              </div>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                        <div className="mt-2 flex flex-col items-center gap-1">
                                          <p className="text-xs text-muted-foreground mb-1">
                                            {new Date(item.updated_at).toLocaleString("pt-BR", {
                                              day: "2-digit",
                                              month: "2-digit",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </p>
                                          <p className="text-sm font-medium max-w-[120px] text-center">
                                            Editado
                                          </p>
                                          <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                                            <Pencil className="w-3 h-3" />
                                            Por: {item.updated_by.name}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                </React.Fragment>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </If>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
          {dataApi?.data.length < 1 && (
            <TableRow>
              <TableCell colSpan={10} className="text-center py-8">
                <p className="text-gray-500">Nada foi encontrado.</p>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {selectedStep && (
        <StepDialog
          isOpen={!!selectedStep}
          onClose={() => {
            setSelectedStep(null);
            setSelectedStepSale(null);
          }}
          stepId={selectedStep.id}
          stepName={selectedStep.sale_step.name}
          currentStatus={selectedStep.process_status}
          onUpdateStatus={(statusId, trackingCode) =>
            handleUpdateStatus(selectedStep.id, statusId, trackingCode)
          }
          saleStepId={selectedStep.sale_step.id}
          saleTypeId={selectedStepSale?.sale_type_id}
          trackingCode={selectedStepSale?.tracking_code || undefined}
          chats={selectedStep.process_step_sale_chats}
          onChatAdded={handleRefresh}
        />
      )}

      {selectedSale && (
        <PixDialog
          isOpen={!!selectedSale}
          onClose={() => setSelectedSale(null)}
          sale={selectedSale}
          onPaymentConfirmed={handleRefresh}
        />
      )}

      <Dialog
        open={!!paymentDetailsSale}
        onOpenChange={(open) => !open && setPaymentDetailsSale(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Detalhes do Pagamento</DialogTitle>
          </DialogHeader>
          {paymentDetailsSale && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                Venda #{paymentDetailsSale.id} - {paymentDetailsSale.client?.name}
              </div>
              {paymentDetailsSale.payment_forms?.map((pf) => (
                <div key={pf.id} className="p-3 bg-muted/50 rounded-lg border text-sm space-y-1">
                  <div className="font-medium">
                    {pf.payment_form?.name || "—"} - {Mask.formatCurrency(parseFloat(pf.value))}
                  </div>
                  {pf.sale_code_transaction && pf.sale_code_transaction !== "0" && (
                    <div><b>Código:</b> {pf.sale_code_transaction}</div>
                  )}
                  {pf.payer_name && (
                    <div><b>Pagador:</b> {pf.payer_name}</div>
                  )}
                  {pf.date_hour && (
                    <div>
                      <b>Data/Hora:</b>{" "}
                      {(() => {
                        try {
                          return format(new Date(pf.date_hour), "dd/MM/yyyy HH:mm");
                        } catch {
                          return pf.date_hour;
                        }
                      })()}
                    </div>
                  )}
                  {pf.observation && (
                    <div><b>Obs:</b> {pf.observation}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ReceiptDialog
        sale={selectedSale}
        open={showReceipt}
        onOpenChange={setShowReceipt}
      />

      <TransactionCodeModal
        isOpen={showTransactionModal}
        onClose={() => setShowTransactionModal(false)}
        onConfirm={handleTransactionConfirm}
        salePaymentForms={saleForApproval?.payment_forms?.filter(
          (form) => form?.payment_form?.id != 3
        )}
        bankAccounts={bankAccounts}
        saleTypeId={saleForApproval?.sale_type_id}
      />

      <EditSaleModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        sale={saleToEdit}
        onSaleUpdated={handleSaleUpdated}
      />

      <CancelSaleModal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        sale={saleToCancel}
        onSaleCancelled={handleSaleCancelled}
      />

      <CancelRequestModal
        isOpen={cancelRequestModalOpen}
        onClose={() => setCancelRequestModalOpen(false)}
        sale={saleForCancelRequest}
        onSuccess={handleRefresh}
      />

      <EditRequestModal
        isOpen={editRequestModalOpen}
        onClose={() => setEditRequestModalOpen(false)}
        sale={saleForEditRequest}
        onSuccess={handleRefresh}
      />

      <EndorsementRequestModal
        isOpen={endorsementSaleId !== null}
        onClose={() => setEndorsementSaleId(null)}
        saleId={endorsementSaleId}
        onSuccess={loadEndorsementStatuses}
      />

      {/* PIX Payment Modal */}
      {selectedPixTransaction && (
        <Dialog
          open={showPixPaymentModal}
          onOpenChange={(open) => !open && handleClosePixModal()}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {selectedPixTransaction?.status === PIX_STATUS.PAID
                  ? "Pagamento Confirmado"
                  : "Pagamento PIX"}
              </DialogTitle>
            </DialogHeader>
            <PixPayment
              transaction={selectedPixTransaction}
              saleId={selectedPixTransaction.saleId}
              onPaymentConfirmed={handlePixPaymentConfirmed}
              onClose={handleClosePixModal}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* PIX Selection Modal */}
      <Dialog
        open={showPixSelectionModal}
        onOpenChange={(open) => !open && handleClosePixSelectionModal()}
      >
        <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Selecionar Transação PIX</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 overflow-y-auto flex-1 pr-2">
            <p className="text-sm text-gray-600">
              Esta venda possui múltiplas transações PIX. Selecione qual deseja
              visualizar:
            </p>
            {availablePixTransactions.map((transaction, index) => (
              <Button
                key={transaction.id}
                variant="outline"
                className="w-full h-auto p-4 flex-col items-start bg-white hover:bg-gray-100 border-gray-200"
                onClick={() => handleSelectPixTransaction(transaction)}
              >
                <div className="flex justify-between items-center w-full mb-2">
                  <span className="font-medium text-black">
                    PIX #{index + 1}
                  </span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      transaction.status === PIX_STATUS.PAID
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {transaction.status === PIX_STATUS.PAID
                      ? "Pago"
                      : "Pendente"}
                  </span>
                </div>
                <div className="text-left w-full">
                  <p className="text-sm text-gray-600">
                    Valor: {Mask.formatCurrency(parseFloat(transaction.amount))}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    ID: {transaction.id_transaction}
                  </p>
                </div>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Links Modal - Multiple PIX */}
      <Dialog
        open={showPaymentLinksModal}
        onOpenChange={(open) => !open && handleClosePaymentLinksModal()}
      >
        <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Links de Pagamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 overflow-y-auto flex-1 pr-2">
            <p className="text-sm text-gray-600">
              Esta venda possui múltiplos pagamentos PIX. Copie o link desejado:
            </p>
            {saleForPaymentLinks?.transactions
              ?.filter((t) => t.pix_copy_and_paste)
              .map((transaction, index) => (
                <div
                  key={transaction.id}
                  className="p-4 border rounded-lg bg-white hover:bg-gray-50"
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-black">
                      PIX #{index + 1}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        transaction.status === PIX_STATUS.PAID
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {transaction.status === PIX_STATUS.PAID
                        ? "Pago"
                        : "Pendente"}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Valor: {Mask.formatCurrency(parseFloat(transaction.amount))}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2"
                    onClick={() => handleCopySinglePaymentLink(index)}
                  >
                    <Link className="h-4 w-4" />
                    Copiar Link
                  </Button>
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex justify-between items-center mt-4">
        {dataApi?.data.length > 0 && (
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Página {dataApi?.current_page} de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
